import React, { useState } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Divider,
  IconButton,
  Box,
  CardMedia,
  CircularProgress,
  Modal,
  TextField,
  InputAdornment,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { crearTrace } from '../actions/TraceAction';

const PropertyDetailView = ({ property, onClose, onPurchaseSuccess }) => {
    const imagenDefault = "https://firebasestorage.googleapis.com/v0/b/ecommerce-a78e2.appspot.com/o/images%2Fmixer-default.jpg?alt=media&token=1d0f686e-4c1d-41eb-a247-a75983439815";
    const [openModal, setOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [updatedProperty, setUpdatedProperty] = useState(property);

    // Estado para los datos de la venta
    const [saleData, setSaleData] = useState({
        date: new Date(),
        name: '',
        value: property.price || 0,
        tax: 0,
    });

    // Calcula el total
    const calculateTotal = (value, tax) => {
        const numericValue = parseFloat(value) || 0;
        const numericTax = parseFloat(tax) || 0;
        return (numericValue * (1 + numericTax / 100)).toFixed(2);
    };

    // Maneja cambios en los campos
    const handleSaleDataChange = (field, value) => {
        setSaleData(prev => ({
        ...prev,
        [field]: value
        }));
    };

    // Abre el modal de confirmación
    const handleOpenPurchaseModal = () => {
        setSaleData(prev => ({
        ...prev,
        value: property.price || 0
        }));
        setOpenModal(true);
    };

    // Cierra el modal
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Maneja el éxito de la compra
    const handlePurchaseSuccess = (newOwnerData) => {
        // Actualizar los datos locales
        const updatedData = {
            ...updatedProperty,
            ownerName: newOwnerData.name,
            ownerId: newOwnerData.id
        };
        
        setUpdatedProperty(updatedData);
        
        // Notificar al componente padre
        if (onPurchaseSuccess) {
            onPurchaseSuccess(updatedData);
        }
    };

    // Procesa la compra
    const handleConfirmPurchase = async () => {
        setIsLoading(true);
        try {
            // Formatear la fecha a ISO 8601 (YYYY-MM-DDTHH:mm:ss.sssZ)
            const formattedDate = saleData.date.toISOString();
            
            // Datos que se enviarán a la API
            const propertyTraceData = {
                id: '0',
                propertyId: property.id,
                dateSale: formattedDate,
                name: saleData.name,
                value: saleData.value,
                tax: saleData.tax,
                // Agrega otros campos necesarios
            };

            console.log('request propertytracedata a grabar: ', propertyTraceData);

            const response = await crearTrace(propertyTraceData);

            console.log('response graba trace: ', response);

            if (response.status === 201) {
        
                console.log('response creatrace:', response);

                // Notificar éxito
                handlePurchaseSuccess({
                    id: response.data.ownerId || 'new-owner-id', // Asegúrate que tu API devuelva esto
                    name: saleData.name 
                });

                setSnackbarMessage('Compra registrada exitosamente');

                handleCloseModal();
            }

        } catch (error) {
            console.error('Error:', error);
            setSnackbarMessage(error.message || 'Error al procesar la compra');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>

            {/* Box contenedor principal (centra el Card) */}
            <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            p: 2
            }}>

                {/* Card contenedor */}
                    <Card sx={{
                        width: '100%',
                        maxWidth: 1200,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }, // Columna en móvil, fila en desktop
                        overflow: 'hidden'
                    }}>
                        {/* Sección izquierda - Datos (4/12) */}
                        <Box sx={{
                        flex: 4,  // Equivale a 4 columnas
                        p: 3,
                        overflowY: 'auto'
                        }}>
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                            {updatedProperty.evento || updatedProperty.name}
                            </Typography>
                
                            <Box sx={{ mb: 3, flexGrow: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Información básica
                                </Typography>
                                
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Código:
                                        </Typography>
                                        <Typography variant="body1">
                                            {property.codeInternal || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Año:
                                        </Typography>
                                        <Typography variant="body1">
                                            {property.year || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                <Typography variant="body2" color="text.secondary">
                                    Dirección:
                                </Typography>
                                <Typography variant="body1" paragraph>
                                    {property.address || 'N/A'}
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary">
                                    Propietario:
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                                    {updatedProperty.ownerName || 'N/A'}
                                </Typography>
                            </Box>
            
                            <Divider sx={{ my: 2 }} />
                
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Precio
                                </Typography>
                                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                    ${updatedProperty.price?.toLocaleString() || 'N/A'}
                                </Typography>
                            </Box>
                
                            <Button 
                                variant="contained" 
                                size="large" 
                                fullWidth
                                sx={{ 
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                mb: 2
                                }}
                                onClick={handleOpenPurchaseModal}
                                disabled={isLoading} // Deshabilitar durante la carga
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Comprar ahora'}
                            </Button>

                            {/* Botón "Volver al listado" */}
                            <Button 
                            variant="outlined" 
                            fullWidth
                            onClick={onClose}
                            sx={{
                                py: 1
                            }}
                            >
                                Volver al listado
                            </Button>
                        </Box>



                        {/* Sección derecha - Imagen (8/12) */}
                        <Box sx={{
                        flex: 8,  // Equivale a 8 columnas
                        maxHeight: '70vh', // Limitar altura máxima al 70% del viewport
                        // minHeight: { xs: 300, md: 'auto' }
                        minHeight: { xs: 300, md: 400 }
                        }}>
                            <CardMedia
                                component="img"
                                    image={property.imageUrls?.[0] || imagenDefault}
                                    alt={property.name}
                                    sx={{
                                    height: '100%', //{ xs: 300, md: 600 },
                                    width: '100%',
                                    display: 'block', // Obligatorio
                                    objectFit: 'cover',
                                    bgcolor: '#f5f5f5' // Fondo si falla la imagen
                                    }}
                                    onError={(e) => {
                                    e.target.src = imagenDefault;
                                    }}
                            />
                        </Box>
                    </Card>
            </Box>

            {/* Modal de confirmación de compra */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="purchase-modal-title"
                sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                }}
            >
                <Box sx={{
                width: '90%',
                maxWidth: 800,
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 1,
                maxHeight: '90vh',
                overflowY: 'auto'
                }}>
                    <Typography id="purchase-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
                        Confirmar Compra de {updatedProperty.name || 'Propiedad'}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Fecha */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Fecha"
                                value={saleData.date}
                                onChange={(newValue) => handleSaleDataChange('date', newValue)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Nombre */}
                        <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nombre del comprador"
                                value={saleData.name}
                                onChange={(e) => handleSaleDataChange('name', e.target.value)}
                            />
                        </Grid>
                        
                        {/* Valor */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Valor"
                                type="number"
                                value={saleData.value}
                                onChange={(e) => handleSaleDataChange('value', e.target.value)}
                                inputProps={{ step: "0.01" }}
                            />
                        </Grid>
                        
                        {/* Impuesto */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Tax %"
                                type="number"
                                value={saleData.tax}
                                onChange={(e) => handleSaleDataChange('tax', e.target.value)}
                                InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                inputProps={{ 
                                step: "0.1",
                                min: "0",
                                max: "100"
                                }}
                            />
                        </Grid>
                        
                        {/* Total (calculado) */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Total"
                                value={calculateTotal(saleData.value, saleData.tax)}
                                InputProps={{
                                readOnly: true,
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                            />
                        </Grid>
                    </Grid>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button 
                        variant="outlined" 
                        onClick={handleCloseModal}
                        disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button 
                        variant="contained" 
                        onClick={handleConfirmPurchase}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                        >
                            Confirmar Compra
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Snackbar para mensajes */}
            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={6000}
                onClose={() => setSnackbarMessage('')}
                message={snackbarMessage}
            />
        </LocalizationProvider>
    );
};

export default PropertyDetailView;